import { Injectable } from '@angular/core';
import { GridApi, GridOptions } from 'ag-grid-community';

import { TableViewCustomModel } from './common/model/table-view-custom-model';
import { TableViewCustomModelOption } from './common/model/table-view-custom-model-option';
import { TvaCustomSelectionComponent } from './component-selection/tva-custom-selection.component';
import { TvaCustomSidebarComponent } from './component-sidebar/tva-custom-sidebar.component';

/**
 * Additional infomrations: see Confluence (Table View Administration (Custom))
 */
@Injectable({
  providedIn: 'root',
})
export class TvaCustomService {
  private tvaCustomSelectionComponent: TvaCustomSelectionComponent;
  public tvaCustomSidebarComponent: TvaCustomSidebarComponent;

  public tableViewAreaEnumVal: string;
  public tableViewOptions: TableViewCustomModelOption[];
  public isTableWithPivotFunctionality = false;
  public gridOptions: GridOptions;
  public gridApi: GridApi;

  public readonly CONST_SYSTEM_DEFAULT_TABLE_VIEW: TableViewCustomModelOption = null;
  private isSessionModelEqualCustomModel: boolean;

  public init(
    tableViewAreaEnumVal: string,
    listTableViews: TableViewCustomModel[],
    isTableWithPivotFunctionality: boolean,
    gridOptions: GridOptions,
    gridApi: GridApi,
  ): void {
    this.tableViewAreaEnumVal = tableViewAreaEnumVal;
    this.isTableWithPivotFunctionality = isTableWithPivotFunctionality;
    this.gridOptions = gridOptions;
    this.gridApi = gridApi;

    this.doMapTableView(listTableViews);
  }

  public doMapTableView(listTableViews: TableViewCustomModel[]): void {
    this.tableViewOptions = [];
    const len = listTableViews.length;
    for (let i = 0; i < len; i++) {
      this.tableViewOptions.push({
        key: listTableViews[i].id,
        value: listTableViews[i].tableViewName,
        tableViewArea: listTableViews[i].tableViewArea,
        filterModel: listTableViews[i].filterModel,
        columnState: listTableViews[i].columnState,
        pivotMode: listTableViews[i].pivotMode,
        defaultView: listTableViews[i].defaultView,
        tableViewName: listTableViews[i].tableViewName,
        id: listTableViews[i].id,
      });
    }
  }

  /**
   * Handles the selection.
   * On deleting, editing or opening the page for the first time
   * it must be checked, if a default item exists or if the SystemDefaultView must be displayed.
   */
  public doSelectedTableViewOnComponent(): void {
    const defaultTableView: TableViewCustomModelOption = this.getDefaultTableView();
    this.setSelectedTableViewOnComponent(
      defaultTableView === undefined ? this.CONST_SYSTEM_DEFAULT_TABLE_VIEW : defaultTableView,
    );
  }

  private doResetTableView(): void {
    if (this.gridApi != undefined) {
      this.gridApi.resetColumnState();
      this.gridApi.resetColumnGroupState();
      this.gridApi.applyColumnState({
        state: null,
        applyOrder: false,
        defaultState: { sort: null },
      });

      this.gridApi.updateGridOptions({ pivotMode: this.isTableWithPivotFunctionality });

      this.gridApi.collapseAll();
      this.gridApi.setFilterModel(null);
      this.gridApi.sizeColumnsToFit();
    }
  }

  /**
   * Controls the True-/False-Flag of the ResetState(CustomDefault)-Button if the button is disabled or not
   */
  public doCheckResetDefaultTableViewActivability(): void {
    // Conditions to enable the button:
    // - There must be a customDefaultTableView available
    // - The currently selected value is SystemDefault or not the customTableView which is the default view
    const defaultTableView = this.getDefaultTableView();
    const selectedTableView = this.getSelectedTableView();

    // Disable the reset button if there's no default table view
    if (!defaultTableView) {
      this.tvaCustomSidebarComponent.isBtnResetDefaultTableViewDisabled = true;
      return;
    }
    // Check if the selected table view is the default view
    const isSelectedTableViewDefault =
      selectedTableView && selectedTableView.defaultView && this.isSessionModelEqualCustomModel;
    // Disable the button if the selected table view is the default view, otherwise enable it
    this.tvaCustomSidebarComponent.isBtnResetDefaultTableViewDisabled = isSelectedTableViewDefault;
  }

  public getDefaultTableView(): TableViewCustomModelOption {
    return this.tableViewOptions.find(tableViewCustomModelOption => tableViewCustomModelOption.defaultView === true);
  }

  public getSelectedTableView(): TableViewCustomModelOption {
    let selectedTableView: TableViewCustomModelOption = null;
    const selectedTableViewName: TableViewCustomModelOption =
      this.tvaCustomSelectionComponent.formGroupSelection.controls.formControlSelection.value;
    if (selectedTableViewName) {
      selectedTableView = this.tableViewOptions.find(
        tableViewCustomModelOption => tableViewCustomModelOption.tableViewName === selectedTableViewName.tableViewName,
      );
    }
    return selectedTableView;
  }

  public setTvaCustomSelectionComponent(tvaCustomSelectionComponent: TvaCustomSelectionComponent): void {
    this.tvaCustomSelectionComponent = tvaCustomSelectionComponent;
  }

  public setTvaCustomSidebarComponent(tvaCustomSidebarComponent: TvaCustomSidebarComponent): void {
    this.tvaCustomSidebarComponent = tvaCustomSidebarComponent;
  }

  /**
   * Set selection value on HTML-Selection-Component directly (without doSelectedTableViewOnComponentHandling ;)
   * E.g after creating a new Item.
   * After saving the new item should be selected.
   */
  public setSelectedTableViewOnComponent(value: TableViewCustomModelOption): void {
    this.setSelectedTableViewOnlyOnSelectionComponent(value, true);
    if (value == this.CONST_SYSTEM_DEFAULT_TABLE_VIEW) {
      this.doResetTableView();
    } else {
      this.tvaCustomSelectionComponent.onSelectTableView(value);
    }
    this.doCheckMultipleComponentBehavior();
  }

  public setSelectedTableViewOnlyOnSelectionComponent(
    tableViewCustomModel: TableViewCustomModel,
    isSessionModelEqualCustomModel: boolean,
  ): void {
    this.tvaCustomSelectionComponent.formGroupSelection = this.tvaCustomSelectionComponent.fb.group({
      formControlSelection: tableViewCustomModel,
    });
    this.isSessionModelEqualCustomModel = isSessionModelEqualCustomModel;
    this.doCheckMultipleComponentBehavior();
  }

  private doCheckMultipleComponentBehavior(): void {
    this.doCheckResetDefaultTableViewActivability();

    const formGroupSelection = this.tvaCustomSelectionComponent.formGroupSelection;
    this.tableViewOptions.length > 0 ? formGroupSelection.enable() : formGroupSelection.disable();
  }
}
